import { useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  /** The images of the one comment or note that was clicked, in display order. */
  images: string[];
  /** The image shown; null means closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
};

const ROUND_BUTTON_CLASS = "grid place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20";

/** Keeps a click on a control from reaching the content, where it would close the preview. */
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * Full-size preview of a comment's or note's images. A Radix dialog of its own,
 * so it stacks above drawers and modals (TaskOverviewModal,
 * DeleteConfirmationModal) and ESC or a click on the backdrop closes only the
 * preview. With two or more images: arrow buttons, ← / → keys, a counter and a
 * thumbnail strip, all wrapping around.
 */
const ImageLightbox = ({ images, index, onIndexChange, onClose }: Props) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const count = images.length;
  const open = index !== null && count > 0;
  const current = open ? Math.min(Math.max(index, 0), count - 1) : 0;
  const src = open ? images[current] : null;
  const multiple = count > 1;
  const next = (current + 1) % count;
  const prev = (current - 1 + count) % count;

  // Warm the neighbours so the arrows feel instant.
  useEffect(() => {
    if (!open || !multiple) return;
    new Image().src = images[next];
    new Image().src = images[prev];
  }, [open, multiple, images, next, prev]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!multiple || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
    // Nothing behind the preview (an editor cursor, a feed) moves.
    e.preventDefault();
    e.stopPropagation();
    onIndexChange(e.key === "ArrowRight" ? next : prev);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[250] bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onClick={onClose}
          onKeyDown={onKeyDown}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            closeRef.current?.focus();
          }}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "fixed inset-0 z-[250] grid place-items-center outline-none",
            multiple ? "px-16 pb-24 pt-16" : "p-8"
          )}
        >
          <DialogPrimitive.Title className="sr-only">Image preview</DialogPrimitive.Title>

          <div className="absolute right-5 top-5 flex items-center gap-2">
            {src && (
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open original"
                title="Open original"
                onClick={stop}
                className={cn(ROUND_BUTTON_CLASS, "h-9 w-9")}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            <button
              ref={closeRef}
              type="button"
              aria-label="Close preview"
              onClick={onClose}
              className={cn(ROUND_BUTTON_CLASS, "h-9 w-9")}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {multiple && (
            <div
              aria-live="polite"
              onClick={stop}
              className="absolute left-1/2 top-6 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-[12px] font-medium tabular-nums text-white"
            >
              {current + 1} / {count}
            </div>
          )}

          {src && (
            <img
              src={src}
              alt={multiple ? `Attachment ${current + 1} of ${count}` : "Attachment"}
              onClick={stop}
              // Capped by the viewport minus the content padding: a percentage cap
              // doesn't resolve in the auto-sized grid row, so a large photo would
              // render at its natural size and spill off-screen.
              className={cn(
                "rounded-xl object-contain shadow-2xl",
                multiple
                  ? "max-h-[calc(100dvh-10rem)] max-w-[calc(100vw-8rem)]"
                  : "max-h-[calc(100dvh-4rem)] max-w-[calc(100vw-4rem)]"
              )}
            />
          )}

          {multiple && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                onClick={(e) => {
                  stop(e);
                  onIndexChange(prev);
                }}
                className={cn(ROUND_BUTTON_CLASS, "absolute left-4 top-1/2 h-10 w-10 -translate-y-1/2")}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={(e) => {
                  stop(e);
                  onIndexChange(next);
                }}
                className={cn(ROUND_BUTTON_CLASS, "absolute right-4 top-1/2 h-10 w-10 -translate-y-1/2")}
              >
                <ChevronRight className="h-5 w-5" />
              </button>

              <div
                onClick={stop}
                className="absolute bottom-5 left-1/2 flex max-w-[calc(100%-2rem)] -translate-x-1/2 gap-2 overflow-x-auto p-1"
              >
                {images.map((thumb, i) => (
                  <button
                    key={`${thumb}-${i}`}
                    type="button"
                    aria-label={`Show image ${i + 1}`}
                    aria-current={i === current}
                    onClick={() => onIndexChange(i)}
                    className={cn(
                      "h-12 w-12 shrink-0 overflow-hidden rounded-md transition",
                      i === current ? "ring-2 ring-white" : "opacity-60 hover:opacity-100"
                    )}
                  >
                    <img src={thumb} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default ImageLightbox;
